import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  StorageReference,
} from 'firebase/storage';
import { db, storage } from './firebase.config';
import {
  DriveFile,
  DriveStats,
  DriveFolder,
  MAX_FILE_SIZE,
  MAX_TOTAL_SIZE,
  SUPPORTED_FILE_TYPES,
  sanitizeFileName,
} from '../types/drive';

class DriveService {
  private getDriveFilesCollection() {
    return collection(db, 'userDriveFiles');
  }

  private getDriveStatsCollection() {
    return collection(db, 'userDriveStats');
  }

  private getDriveStatsDoc(userId: string) {
    return doc(db, 'userDriveStats', userId);
  }

  private getStorageRef(userId: string, folder: DriveFolder, fileName: string): StorageReference {
    const path = `users/${userId}/drive/${folder}/${fileName}`;
    return ref(storage, path);
  }

  // Verifica se l'utente ha spazio disponibile per un nuovo file
  async checkSpaceAvailable(userId: string, fileSize: number): Promise<{ available: boolean; currentSize: number; remaining: number }> {
    try {
      const statsDoc = await getDoc(this.getDriveStatsDoc(userId));
      const currentSize = statsDoc.exists() ? statsDoc.data().totalSize : 0;
      const remaining = MAX_TOTAL_SIZE - currentSize;
      const available = remaining >= fileSize;

      return {
        available,
        currentSize,
        remaining,
      };
    } catch (error) {
      console.error('Errore nel controllo spazio disponibile:', error);
      throw new Error('Impossibile verificare lo spazio disponibile');
    }
  }

  // Carica un file e salva i metadati
  async uploadFile(
    userId: string,
    file: File,
    folder: DriveFolder,
    customFolder?: string
  ): Promise<DriveFile> {
    try {
      // Validazione file
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File troppo grande. Massimo ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
      }

      if (!SUPPORTED_FILE_TYPES[file.type as keyof typeof SUPPORTED_FILE_TYPES]) {
        throw new Error('Tipo di file non supportato');
      }

      // Verifica spazio disponibile
      const spaceCheck = await this.checkSpaceAvailable(userId, file.size);
      if (!spaceCheck.available) {
        throw new Error('Spazio insufficiente. Elimina alcuni file per liberare spazio.');
      }

      // Genera nome file unico
      const timestamp = Date.now();
      const sanitizedName = sanitizeFileName(file.name);
      const fileName = `${timestamp}_${sanitizedName}`;

      // Carica file su Storage
      const storageRef = this.getStorageRef(userId, folder, fileName);
      const uploadResult = await uploadBytes(storageRef, file);

      // Ottieni URL download
      const downloadURL = await getDownloadURL(uploadResult.ref);

      // Crea metadati file
      const fileData: Omit<DriveFile, 'id'> = {
        userId,
        fileName,
        originalName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        storagePath: uploadResult.ref.fullPath,
        folder,
        customFolder: customFolder || null, // Converti undefined in null per Firestore
        uploadedAt: Timestamp.now(),
        uploadedBy: userId,
        downloadURL,
      };

      // Salva metadati in Firestore
      const docRef = await addDoc(this.getDriveFilesCollection(), fileData);

      // Aggiorna statistiche utente
      try {
        await this.updateUserStats(userId, file.size, 1);
      } catch (statsError) {
        console.warn('Errore nell\'aggiornamento statistiche:', statsError);
        // Non blocchiamo l'upload se falliscono le statistiche
      }

      return {
        id: docRef.id,
        ...fileData,
      };
    } catch (error) {
      console.error('Errore nel caricamento file:', error);
      throw error;
    }
  }

  // Elimina un file
  async deleteFile(fileId: string, userId: string): Promise<void> {
    try {
      // Ottieni metadati file
      const filesQuery = query(
        this.getDriveFilesCollection(),
        where('__name__', '==', fileId),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(filesQuery);

      if (querySnapshot.empty) {
        throw new Error('File non trovato');
      }

      const fileDoc = querySnapshot.docs[0];
      const fileData = fileDoc.data() as DriveFile;

      // Elimina file da Storage
      const storageRef = ref(storage, fileData.storagePath);
      await deleteObject(storageRef);

      // Elimina metadati da Firestore
      await deleteDoc(fileDoc.ref);

      // Aggiorna statistiche utente
      await this.updateUserStats(userId, -fileData.fileSize, -1);
    } catch (error) {
      console.error('Errore nell\'eliminazione file:', error);
      throw error;
    }
  }

  // Ottieni URL download per un file
  async getDownloadURL(storagePath: string): Promise<string> {
    try {
      const storageRef = ref(storage, storagePath);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('Errore nell\'ottenimento URL download:', error);
      throw new Error('Impossibile ottenere l\'URL di download');
    }
  }

  // Ottieni file di un utente
  async getUserFiles(userId: string): Promise<DriveFile[]> {
    try {
      // Prima prova con l'indice composito
      const filesQuery = query(
        this.getDriveFilesCollection(),
        where('userId', '==', userId),
        orderBy('uploadedAt', 'desc')
      );
      const querySnapshot = await getDocs(filesQuery);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as DriveFile[];
    } catch (error: any) {
      console.error('Errore nel recupero file utente:', error);
      
      // Se l'errore è relativo all'indice, prova senza orderBy
      if (error.code === 'failed-precondition' && error.message.includes('index')) {
        try {
          console.log('Tentativo fallback senza orderBy...');
          const fallbackQuery = query(
            this.getDriveFilesCollection(),
            where('userId', '==', userId)
          );
          const fallbackSnapshot = await getDocs(fallbackQuery);
          
          const files = fallbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as DriveFile[];
          
          // Ordina manualmente
          return files.sort((a, b) => b.uploadedAt.seconds - a.uploadedAt.seconds);
        } catch (fallbackError) {
          console.error('Errore anche nel fallback:', fallbackError);
        }
      }
      
      throw new Error('Impossibile recuperare i file');
    }
  }

  // Ottieni statistiche utente
  async getUserStats(userId: string): Promise<DriveStats | null> {
    try {
      const statsDoc = await getDoc(this.getDriveStatsDoc(userId));
      if (statsDoc.exists()) {
        return statsDoc.data() as DriveStats;
      }
      return null;
    } catch (error) {
      console.error('Errore nel recupero statistiche utente:', error);
      throw new Error('Impossibile recuperare le statistiche');
    }
  }

  // Aggiorna statistiche utente
  async updateUserStats(userId: string, sizeDelta: number, countDelta: number): Promise<void> {
    try {
      const statsRef = this.getDriveStatsDoc(userId);
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        const currentStats = statsDoc.data() as DriveStats;
        await updateDoc(statsRef, {
          totalSize: Math.max(0, currentStats.totalSize + sizeDelta),
          fileCount: Math.max(0, currentStats.fileCount + countDelta),
          lastUpdated: Timestamp.now(),
        });
      } else {
        // Crea nuove statistiche se non esistono
        await setDoc(statsRef, {
          userId,
          totalSize: Math.max(0, sizeDelta),
          fileCount: Math.max(0, countDelta),
          lastUpdated: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento statistiche:', error);
      throw new Error('Impossibile aggiornare le statistiche');
    }
  }

  // Ottieni tutti i file (per admin/moderatori)
  async getAllFiles(): Promise<DriveFile[]> {
    try {
      const filesQuery = query(
        this.getDriveFilesCollection(),
        orderBy('uploadedAt', 'desc')
      );
      const querySnapshot = await getDocs(filesQuery);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as DriveFile[];
    } catch (error) {
      console.error('Errore nel recupero tutti i file:', error);
      throw new Error('Impossibile recuperare i file');
    }
  }

  // Listener in tempo reale per file utente
  subscribeToUserFiles(userId: string, callback: (files: DriveFile[]) => void) {
    try {
      const filesQuery = query(
        this.getDriveFilesCollection(),
        where('userId', '==', userId),
        orderBy('uploadedAt', 'desc')
      );

      return onSnapshot(filesQuery, (snapshot) => {
        const files = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as DriveFile[];
        callback(files);
      }, (error) => {
        console.error('Errore nel listener file:', error);
        // In caso di errore, prova senza orderBy
        if (error.code === 'failed-precondition' && error.message.includes('index')) {
          console.log('Tentativo listener fallback senza orderBy...');
          const fallbackQuery = query(
            this.getDriveFilesCollection(),
            where('userId', '==', userId)
          );
          
          return onSnapshot(fallbackQuery, (snapshot) => {
            const files = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
            })) as DriveFile[];
            // Ordina manualmente
            const sortedFiles = files.sort((a, b) => b.uploadedAt.seconds - a.uploadedAt.seconds);
            callback(sortedFiles);
          });
        }
      });
    } catch (error) {
      console.error('Errore nel setup listener file:', error);
      // Ritorna una funzione vuota per evitare errori
      return () => {};
    }
  }

  // Listener in tempo reale per statistiche utente
  subscribeToUserStats(userId: string, callback: (stats: DriveStats | null) => void) {
    const statsRef = this.getDriveStatsDoc(userId);

    return onSnapshot(statsRef, (doc) => {
      if (doc.exists()) {
        callback(doc.data() as DriveStats);
      } else {
        callback(null);
      }
    });
  }
}

export const driveService = new DriveService(); 